import DownloadIcon from "@mui/icons-material/Download";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import * as XLSX from "xlsx";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SEVERITY_ORDER = { CRITICAL: 0, HIGH: 1, MEDIUM: 2 };
const ROWS_INITIAL = 10;

const CHIP_SX = {
  CRITICAL: {
    backgroundColor: "var(--dangerColor)",
    color: "#fff",
    fontWeight: 700,
  },
  HIGH: {
    backgroundColor: "var(--warningColor)",
    color: "#fff",
    fontWeight: 700,
  },
  MEDIUM: {
    backgroundColor: "var(--primaryColor)",
    color: "#fff",
    fontWeight: 700,
  },
};

const ISSUE_LABELS = {
  MISSING_UUID: "Missing UUID",
  INVALID_UUID_FORMAT: "Invalid UUID format",
  DUPLICATE_UUID_GROUP: "Duplicate UUID (whole group excluded)",
  MISSING_EMPLOYER: "Missing employer name",
  MISSING_ID_NUMBER: "Missing ID Number",
  MISSING_EMP_NUMBER: "Missing employee number",
  MISSING_CELL_NUMBER: "Missing cell number",
  DUPLICATE_ID_NUMBER: "Duplicate ID Number",
  DUPLICATE_EMP_NUMBER: "Duplicate employee number",
  DUPLICATE_CELL_NUMBER: "Duplicate cell number",
  INVALID_DATE_FORMAT_BIRTH_DATE:
    "Invalid Birth Date format (expected YYYY/MM/DD)",
  INVALID_DATE_FORMAT_EMPLOYMENT_DATE:
    "Invalid Employment Date format (expected YYYY/MM/DD)",
  INVALID_DATE_FORMAT_TERMINATION_DATE:
    "Invalid Termination Date format (expected YYYY/MM/DD)",
  IDENTITY_CONFLICT: "Employee identity could not be safely matched",
  UUID_ACTIVE_UNDER_DIFFERENT_EMPLOYER:
    "UUID is active under a different employer",
  EMPLOYER_NOT_FOUND: "Employer was not found",
  INVALID_BIRTH_DATE: "Invalid Birth Date",
  INVALID_EMPLOYMENT_DATE: "Invalid Employment Date",
  INVALID_TERMINATION_DATE: "Invalid Termination Date",
  SERVER_VALIDATION_ERROR: "Server validation error",
};

const ISSUE_ACTIONS = {
  IDENTITY_CONFLICT:
    "Review the uploaded row against the active LMA employee and Nova identity. Correct the inconsistent payroll, LMA or Nova identity data, then upload the corrected rows again.",
  UUID_ACTIVE_UNDER_DIFFERENT_EMPLOYER:
    "Review the current and payroll employers. Choose Move to make the payroll employer active and deactivate the current employment, or choose Keep current to leave the existing employment unchanged. Move is available only after Nova verifies the employee identity.",
  EMPLOYER_NOT_FOUND:
    "Correct the employer name to an existing LMA company, then upload the row again.",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Build a grouped summary sorted by severity then count.
 * Each entry includes the list of affected row snippets for the detail panel.
 */
function buildIssueSummary(excludedRows, flaggedRows) {
  const map = new Map();

  const processRows = (rows) => {
    for (const row of rows) {
      for (const issue of row.issues) {
        const snippet = {
          rowIndex: row.rowIndex,
          name:
            [row.data["First Name"], row.data["Last Name"]]
              .filter(Boolean)
              .join(" ") || "—",
          empNumber: String(row.data["Emp Number"] || "—"),
          uuid: String(row.data["UUID"] || "—"),
          idNumber: String(row.data["ID Number"] || "—"),
          cellNumber: String(row.data["Cell Number"] || "—"),
          payrollEmployer: String(row.data["EMPLOYER"] || "—"),
          existingUuid: row.existingUuid || "—",
          existingEmployer: row.existingEmployer || "—",
          conflictFields: row.conflictFields || [],
          sameEmployer: row.sameEmployer,
          sameEmployeeNumber: row.sameEmployeeNumber,
          sameIdNumber: row.sameIdNumber,
          novaDecision: row.novaDecision || "UNAVAILABLE",
          novaReason: row.novaReason || "",
          reason: issue.message || ISSUE_LABELS[issue.code] || issue.code,
          recommendedAction:
            row.recommendedAction ||
            ISSUE_ACTIONS[issue.code] ||
            "Correct the source row and upload it again.",
        };

        const existing = map.get(issue.code);
        if (existing) {
          existing.count += 1;
          existing.rows.push(snippet);
        } else {
          map.set(issue.code, {
            severity: issue.severity,
            count: 1,
            action: issue.exclude ? "Excluded" : "Submitted",
            rows: [snippet],
          });
        }
      }
    }
  };

  processRows(excludedRows);
  processRows(flaggedRows);

  return [...map.entries()]
    .sort(([, a], [, b]) => {
      const sev = SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
      return sev !== 0 ? sev : b.count - a.count;
    })
    .map(([code, info]) => ({ code, ...info }));
}

function downloadFile(file) {
  const url = URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = url;
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function downloadExcludedExcel(excludedRows) {
  const allHeaders = [
    "SOURCE ROW",
    "EMPLOYER",
    "EMPLOYEE NUMBER",
    "CELL NUMBER",
    "INCOMING UUID",
    "EXISTING LMA UUID",
    "EXISTING LMA EMPLOYER",
    "ISSUE",
    "REASON",
    "RECOMMENDED ACTION",
  ];
  const rows = [
    allHeaders,
    ...excludedRows.map((row) => {
      const issue = row.issues[0] || {};
      return [
        row.rowIndex,
        row.data["EMPLOYER"] || "",
        row.data["Emp Number"] || "",
        row.data["Cell Number"] || "",
        row.data["UUID"] || "",
        row.existingUuid || "",
        row.existingEmployer || "",
        issue.code || "",
        issue.message || ISSUE_LABELS[issue.code] || issue.code || "",
        row.recommendedAction ||
          ISSUE_ACTIONS[issue.code] ||
          "Correct the source row and upload it again.",
      ];
    }),
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Conflicts");
  const buffer = XLSX.write(workbook, { type: "array", bookType: "xlsx" });
  downloadFile(
    new File([buffer], "payroll-conflict-report.xlsx", {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
  );
}

// ---------------------------------------------------------------------------
// IssueDetailRows — the expandable sub-table for one issue type
// ---------------------------------------------------------------------------

function IssueDetailRows({ issueCode, rows }) {
  const [page, setPage] = useState(0);
  const pageCount = Math.ceil(rows.length / ROWS_INITIAL);
  const visible = rows.slice(page * ROWS_INITIAL, (page + 1) * ROWS_INITIAL);
  const hasMore = rows.length > ROWS_INITIAL;
  const isEmployerIssue = issueCode === "EMPLOYER_NOT_FOUND";
  const isCrossEmployer = issueCode === "UUID_ACTIVE_UNDER_DIFFERENT_EMPLOYER";
  const isIdentityConflict = issueCode === "IDENTITY_CONFLICT";

  return (
    <Box
      sx={{
        backgroundColor: "var(--thin-greyColor)",
        px: 2,
        pb: 1,
        overflowX: "auto",
      }}
    >
      {isIdentityConflict && (
        <Alert severity="info" sx={{ my: 1 }}>
          <Typography variant="body2" fontWeight={700}>
            Resolve this in the admin records first
          </Typography>
          <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
            1. Compare the payroll UUID, cellphone, employee number and employer
            with the active LMA employee shown below.
            <br />
            2. Check that the same UUID, cellphone and ID number belong to the
            employee in Nova.
            <br />
            3. Correct the incorrect payroll, LMA or Nova identity data. If the
            employee has moved employers, upload the corrected row again. Once
            the identity is verified, the system will show options to move the
            employee to the payroll employer or keep the current employer.
            <br />
            4. Once the inconsistent Nova and LMA identities have been
            corrected, upload the corrected rows again.
          </Typography>
        </Alert>
      )}
      <Table size="small" sx={{ tableLayout: "fixed", minWidth: 820 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", width: 64 }}>
              Row #
            </TableCell>
            <TableCell
              sx={{ fontWeight: 600, fontSize: "0.75rem", width: 170 }}
            >
              Employee
            </TableCell>
            <TableCell
              sx={{ fontWeight: 600, fontSize: "0.75rem", width: 120 }}
            >
              Employee no.
            </TableCell>
            <TableCell
              sx={{ fontWeight: 600, fontSize: "0.75rem", width: 130 }}
            >
              Cellphone
            </TableCell>
            <TableCell
              sx={{ fontWeight: 600, fontSize: "0.75rem", width: 190 }}
            >
              Payroll employer
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
              Issue details
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visible.map((row, i) => (
            <TableRow key={i}>
              <TableCell sx={{ fontSize: "0.8rem" }}>{row.rowIndex}</TableCell>
              <TableCell sx={{ fontSize: "0.8rem" }}>{row.name}</TableCell>
              <TableCell sx={{ fontSize: "0.8rem" }}>{row.empNumber}</TableCell>
              <TableCell sx={{ fontSize: "0.8rem" }}>
                {row.cellNumber}
              </TableCell>
              <TableCell sx={{ fontSize: "0.8rem" }}>
                {row.payrollEmployer}
              </TableCell>
              <TableCell sx={{ fontSize: "0.8rem", overflowWrap: "anywhere" }}>
                {isEmployerIssue ? (
                  "No matching LMA company was found."
                ) : isCrossEmployer ? (
                  <>
                    Active at <strong>{row.existingEmployer}</strong>
                    <br />
                    UUID: {row.uuid}
                  </>
                ) : isIdentityConflict ? (
                  <>
                    <strong>Conflict:</strong>{" "}
                    {(row.conflictFields || []).join(" and ") ||
                      "identity data"}{" "}
                    belongs to an active LMA employee with a different UUID.
                    <br />
                    <strong>Existing LMA record:</strong>{" "}
                    {row.existingEmployer || "—"}; UUID{" "}
                    {row.existingUuid || "—"}
                    <br />
                    <strong>Record checks:</strong> Employer{" "}
                    {row.sameEmployer === true
                      ? "matches"
                      : row.sameEmployer === false
                        ? "differs"
                        : "not checked"}
                    ; employee number{" "}
                    {row.sameEmployeeNumber === true
                      ? "matches"
                      : row.sameEmployeeNumber === false
                        ? "differs"
                        : "not checked"}
                    ; ID number{" "}
                    {row.sameIdNumber === true
                      ? "matches"
                      : row.sameIdNumber === false
                        ? "differs"
                        : "not checked"}
                    .
                    <br />
                    <strong>Nova:</strong> {row.novaDecision}
                    {row.novaReason ? ` — ${row.novaReason}` : ""}
                    <br />
                    <strong>Next action:</strong> {row.recommendedAction}
                  </>
                ) : (
                  <>
                    {row.reason}
                    <br />
                    {row.recommendedAction}
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {hasMore && (
        <Box
          sx={{
            pt: 0.5,
            pb: 0.5,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography variant="caption">
            Rows {page * ROWS_INITIAL + 1}–
            {Math.min((page + 1) * ROWS_INITIAL, rows.length)} of {rows.length}
          </Typography>
          <Button
            size="small"
            variant="text"
            disabled={page === 0}
            onClick={() => setPage((current) => current - 1)}
            sx={{
              fontSize: "0.75rem",
              textTransform: "none",
              color: "var(--primaryColor)",
            }}
          >
            Previous
          </Button>
          <Button
            size="small"
            variant="text"
            disabled={page >= pageCount - 1}
            onClick={() => setPage((current) => current + 1)}
            sx={{
              fontSize: "0.75rem",
              textTransform: "none",
              color: "var(--primaryColor)",
            }}
          >
            Next
          </Button>
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Props:
 *   isValidating  boolean
 *   error         string | undefined
 *   cleanCount    number
 *   excludedRows  Array<{ rowIndex, data, issues }>
 *   flaggedRows   Array<{ rowIndex, data, issues }>
 *   headers       string[]
 *   cleanFile     File | null
 */
const CsvValidationPanel = ({
  isValidating,
  error,
  cleanCount,
  excludedRows,
  flaggedRows,
  cleanFile,
  identityUpdates = [],
  identityUpdatesConfirmed = false,
  onConfirmIdentityUpdates,
  employerTransfers = [],
  employerTransferDecisions = {},
  onEmployerTransferDecision,
  onAllEmployerTransferDecisions,
}) => {
  const [expandedCodes, setExpandedCodes] = useState(new Set());
  const [identityPage, setIdentityPage] = useState(0);
  const identityRowsPerPage = 10;
  const identityPageCount = Math.ceil(
    identityUpdates.length / identityRowsPerPage,
  );
  const visibleIdentityUpdates = identityUpdates.slice(
    identityPage * identityRowsPerPage,
    (identityPage + 1) * identityRowsPerPage,
  );
  const verifiedEmployerTransfers = employerTransfers.filter(
    (transfer) => transfer.verified,
  );
  const allVerifiedTransfersMove =
    verifiedEmployerTransfers.length > 0 &&
    verifiedEmployerTransfers.every(
      (transfer) =>
        employerTransferDecisions[transfer.rowNumber] === "move",
    );
  const allVerifiedTransfersStay =
    verifiedEmployerTransfers.length > 0 &&
    verifiedEmployerTransfers.every(
      (transfer) =>
        employerTransferDecisions[transfer.rowNumber] === "keep",
    );
  const toggleExpand = (code) => {
    setExpandedCodes((prev) => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  };

  // ── Validating spinner ──────────────────────────────────────────────────
  if (isValidating) {
    return (
      <Box className="csv-validation-panel csv-validation-panel--loading">
        <CircularProgress size={20} />
        <Typography variant="body2" sx={{ ml: 1 }}>
          Validating file…
        </Typography>
      </Box>
    );
  }

  // ── Error card ──────────────────────────────────────────────────────────
  if (error) {
    return (
      <Box className="csv-validation-panel">
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // ── Normal result ────────────────────────────────────────────────────────
  const hasIssues =
    excludedRows.length > 0 ||
    flaggedRows.length > 0 ||
    identityUpdates.length > 0 ||
    employerTransfers.length > 0;
  const issueSummary = hasIssues
    ? buildIssueSummary(excludedRows, flaggedRows)
    : [];

  return (
    <Box className="csv-validation-panel">
      {/* Scope banner */}
      <Alert
        icon={<InfoOutlinedIcon fontSize="inherit" />}
        severity="info"
        sx={{ mb: 1.5 }}
      >
        <strong>Excel and server preflight checks are complete.</strong> Rows
        with identity, employer, UUID, cell-number, or employee-number conflicts
        are excluded before submission. No payroll data is changed during
        preflight.
      </Alert>

      {/* Stats row */}
      <Box className="csv-validation-panel__summary">
        <Box className="csv-validation-panel__stat csv-validation-panel__stat--clean">
          <Typography variant="h6" component="span">
            {cleanCount.toLocaleString()}
          </Typography>
          <Typography variant="body2">rows ready to submit</Typography>
        </Box>

        {excludedRows.length > 0 && (
          <Box className="csv-validation-panel__stat csv-validation-panel__stat--excluded">
            <Typography variant="h6" component="span">
              {excludedRows.length.toLocaleString()}
            </Typography>
            <Typography variant="body2">rows excluded</Typography>
          </Box>
        )}

        {employerTransfers.length > 0 && (
          <Box className="csv-validation-panel__stat csv-validation-panel__stat--flagged">
            <Typography variant="h6" component="span">
              {employerTransfers.length.toLocaleString()}
            </Typography>
            <Typography variant="body2">
              rows awaiting employer decision
            </Typography>
          </Box>
        )}

        {flaggedRows.length > 0 && (
          <Box className="csv-validation-panel__stat csv-validation-panel__stat--flagged">
            <Typography variant="h6" component="span">
              {flaggedRows.length.toLocaleString()}
            </Typography>
            <Typography variant="body2">rows flagged (submitted)</Typography>
          </Box>
        )}
      </Box>

      {/* Download actions — separate row below stats */}
      {((cleanFile && cleanCount > 0) || excludedRows.length > 0) && (
        <Box className="csv-validation-panel__actions">
          {cleanFile && cleanCount > 0 && (
            <Box className="csv-validation-panel__download-card csv-validation-panel__download-card--primary">
              <Box className="csv-validation-panel__download-icon">
                <TaskAltIcon fontSize="small" />
              </Box>
              <Box className="csv-validation-panel__download-text">
                <Typography variant="body2" fontWeight={600}>
                  Corrected Excel file
                </Typography>
                <Typography variant="caption">
                  {cleanCount.toLocaleString()} clean rows, ready to re-upload
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => downloadFile(cleanFile)}
                sx={{
                  ml: "auto",
                  backgroundColor: "var(--primaryColor)",
                  "&:hover": { backgroundColor: "var(--hover-primaryColor)" },
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                Download
              </Button>
            </Box>
          )}

          {excludedRows.length > 0 && (
            <Box className="csv-validation-panel__download-card csv-validation-panel__download-card--secondary">
              <Box className="csv-validation-panel__download-icon csv-validation-panel__download-icon--danger">
                <ErrorOutlineIcon fontSize="small" />
              </Box>
              <Box className="csv-validation-panel__download-text">
                <Typography variant="body2" fontWeight={600}>
                  Conflict report
                </Typography>
                <Typography variant="caption">
                  {excludedRows.length.toLocaleString()} rows with reasons and
                  required actions
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => downloadExcludedExcel(excludedRows)}
                sx={{
                  ml: "auto",
                  borderColor: "var(--dangerColor)",
                  color: "var(--dangerColor)",
                  "&:hover": {
                    borderColor: "var(--dangerColor)",
                    backgroundColor: "#fd2b2b0f",
                  },
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                Download
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Grouped issue summary with expandable detail rows */}
      {hasIssues && (
        <Box
          sx={{
            mt: 1.5,
            border: "1px solid var(--greyColor2)",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "var(--thin-greyColor)" }}>
                <TableCell sx={{ fontWeight: 700 }} width={32} />
                <TableCell sx={{ fontWeight: 700 }}>Issue</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Rows affected
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Severity
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {employerTransfers.length > 0 && (
                <>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <IconButton
                        size="small"
                        onClick={() => toggleExpand("EMPLOYER_TRANSFER")}
                      >
                        {expandedCodes.has("EMPLOYER_TRANSFER") ? (
                          <ExpandLessIcon fontSize="small" />
                        ) : (
                          <ExpandMoreIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        Confirm employer transfer
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        VERIFIED_EMPLOYER_TRANSFER
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        Nova verified the employee identity. Review which
                        employer should remain active before submission.
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={700}>
                        {employerTransfers.length.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label="REVIEW"
                        size="small"
                        color="warning"
                        sx={{ fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          justifyContent: "center",
                        }}
                      >
                        <Button
                          size="small"
                          variant={
                            allVerifiedTransfersMove ? "contained" : "outlined"
                          }
                          color="warning"
                          disabled={verifiedEmployerTransfers.length === 0}
                          onClick={() =>
                            onAllEmployerTransferDecisions("move")
                          }
                        >
                          Move all
                        </Button>
                        <Button
                          size="small"
                          variant={
                            allVerifiedTransfersStay ? "contained" : "outlined"
                          }
                          color="warning"
                          disabled={verifiedEmployerTransfers.length === 0}
                          onClick={() =>
                            onAllEmployerTransferDecisions("keep")
                          }
                        >
                          Keep all
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                      <Collapse
                        in={expandedCodes.has("EMPLOYER_TRANSFER")}
                        unmountOnExit
                      >
                        <Box
                          sx={{
                            backgroundColor: "var(--thin-greyColor)",
                            px: 2,
                            pb: 1,
                            overflowX: "auto",
                          }}
                        >
                          <Alert severity="warning" sx={{ my: 1 }}>
                            Moving these employees deactivates the current LMA
                            employment and creates an active employment at the
                            payroll employer. Both changes are committed
                            together.
                          </Alert>
                          <Table size="small" aria-label="Employer transfers">
                            <TableHead>
                              <TableRow>
                                <TableCell>Row</TableCell>
                                <TableCell>Employee</TableCell>
                                <TableCell>UUID</TableCell>
                                <TableCell>Cellphone</TableCell>
                                <TableCell>Current employer</TableCell>
                                <TableCell>Payroll employer</TableCell>
                                <TableCell>Employee no.</TableCell>
                                <TableCell>Nova verification</TableCell>
                                <TableCell align="center">Decision</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {employerTransfers.map((item) => (
                                <TableRow
                                  key={`${item.rowNumber}-${item.uuid}`}
                                >
                                  <TableCell>{item.rowNumber}</TableCell>
                                  <TableCell>
                                    {item.employeeName || "—"}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      wordBreak: "break-all",
                                      fontSize: 12,
                                    }}
                                  >
                                    {item.uuid}
                                  </TableCell>
                                  <TableCell>{item.cellNumber}</TableCell>
                                  <TableCell>{item.oldEmployer}</TableCell>
                                  <TableCell>{item.payrollEmployer}</TableCell>
                                  <TableCell>
                                    {item.oldEmployeeNumber || "—"} →{" "}
                                    {item.payrollEmployeeNumber || "—"}
                                  </TableCell>
                                  <TableCell>
                                    {item.verified
                                      ? "ID and cellphone match"
                                      : item.reason ||
                                        "Support review required"}
                                  </TableCell>
                                  <TableCell align="center">
                                    {item.verified ? (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          gap: 0.5,
                                          justifyContent: "center",
                                        }}
                                      >
                                        <Button
                                          size="small"
                                          variant={
                                            employerTransferDecisions[
                                              item.rowNumber
                                            ] === "move"
                                              ? "contained"
                                              : "outlined"
                                          }
                                          color="warning"
                                          onClick={() =>
                                            onEmployerTransferDecision(
                                              item.rowNumber,
                                              "move",
                                            )
                                          }
                                        >
                                          Move
                                        </Button>
                                        <Button
                                          size="small"
                                          variant={
                                            employerTransferDecisions[
                                              item.rowNumber
                                            ] === "keep"
                                              ? "contained"
                                              : "outlined"
                                          }
                                          color="warning"
                                          onClick={() =>
                                            onEmployerTransferDecision(
                                              item.rowNumber,
                                              "keep",
                                            )
                                          }
                                        >
                                          Keep current
                                        </Button>
                                      </Box>
                                    ) : (
                                      <Chip
                                        label="Excluded"
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                      />
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </>
              )}
              {identityUpdates.length > 0 && (
                <>
                  <TableRow
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => toggleExpand("VERIFIED_UUID_CHANGE")}
                  >
                    <TableCell padding="checkbox">
                      <IconButton size="small" tabIndex={-1}>
                        {expandedCodes.has("VERIFIED_UUID_CHANGE") ? (
                          <ExpandLessIcon fontSize="small" />
                        ) : (
                          <ExpandMoreIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        Verified UUID change
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        VERIFIED_UUID_CHANGE
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        Nova matched the payroll UUID to the employee's ID
                        number and cellphone. Review and confirm before
                        submission.
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={700}>
                        {identityUpdates.length.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label="REVIEW"
                        size="small"
                        color="warning"
                        sx={{ fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant={identityUpdatesConfirmed ? "text" : "outlined"}
                        color={identityUpdatesConfirmed ? "success" : "warning"}
                        disabled={identityUpdatesConfirmed}
                        onClick={(event) => {
                          event.stopPropagation();
                          onConfirmIdentityUpdates();
                        }}
                        sx={{ whiteSpace: "nowrap", fontWeight: 700 }}
                      >
                        {identityUpdatesConfirmed
                          ? "Confirmed"
                          : `Confirm ${identityUpdates.length.toLocaleString()}`}
                      </Button>
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ p: 0 }}>
                    <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                      <Collapse
                        in={expandedCodes.has("VERIFIED_UUID_CHANGE")}
                        unmountOnExit
                      >
                        <Box
                          sx={{
                            backgroundColor: "var(--thin-greyColor)",
                            px: 2,
                            pb: 1,
                          }}
                        >
                          <Box sx={{ py: 1 }}>
                            <Typography variant="body2">
                              <strong>What is wrong:</strong> LMA found an
                              active employment record with the same employee
                              number or cellphone, but that record is linked to
                              a different app-user UUID from the one supplied in
                              this payroll file.
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.75 }}>
                              <strong>What Nova verified:</strong> the payroll
                              UUID is an active app-user UUID whose identity
                              number and cellphone match this payroll row. When
                              the old UUID still exists in Nova, its identity
                              number must also match the payroll row. A phone
                              match by itself is never enough.
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.75 }}>
                              <strong>What will change:</strong> after you
                              confirm and submit, LMA will correct the UUID on
                              the existing employment record. Its internal LMA
                              employee ID remains unchanged; no replacement
                              employee record is created.
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.75 }}>
                              <strong>What will not change:</strong> previous
                              training, assignments, questionnaire progress,
                              payroll activity, and audit history remain linked
                              to the same LMA employee record and stay available
                              after the correction. No Nova account or app-user
                              data is changed by this upload.
                            </Typography>
                          </Box>
                          <Box sx={{ overflowX: "auto" }}>
                            <Table
                              size="small"
                              aria-label="Verified UUID changes"
                            >
                              <TableHead>
                                <TableRow>
                                  <TableCell>Row</TableCell>
                                  <TableCell>Employee</TableCell>
                                  <TableCell>Employee no.</TableCell>
                                  <TableCell>Cellphone</TableCell>
                                  <TableCell>Payroll employer</TableCell>
                                  <TableCell>Nova verification</TableCell>
                                  <TableCell>Old UUID</TableCell>
                                  <TableCell>
                                    New verified payroll UUID
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {visibleIdentityUpdates.map((item) => (
                                  <TableRow key={item.rowNumber}>
                                    <TableCell>{item.rowNumber}</TableCell>
                                    <TableCell>{item.employeeName}</TableCell>
                                    <TableCell>{item.employeeNumber}</TableCell>
                                    <TableCell>{item.cellNumber}</TableCell>
                                    <TableCell>
                                      {item.payrollEmployer}
                                    </TableCell>
                                    <TableCell>
                                      {item.payrollIdVerified
                                        ? "ID and cellphone match"
                                        : "Not verified"}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        wordBreak: "break-all",
                                        fontSize: 12,
                                      }}
                                    >
                                      {item.existingUuid}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        wordBreak: "break-all",
                                        fontSize: 12,
                                      }}
                                    >
                                      {item.incomingUuid}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                          {identityPageCount > 1 && (
                            <Box
                              sx={{
                                pt: 0.5,
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography variant="caption">
                                Rows {identityPage * identityRowsPerPage + 1}–
                                {Math.min(
                                  (identityPage + 1) * identityRowsPerPage,
                                  identityUpdates.length,
                                )}{" "}
                                of {identityUpdates.length}
                              </Typography>
                              <Button
                                size="small"
                                disabled={identityPage === 0}
                                onClick={() =>
                                  setIdentityPage((current) => current - 1)
                                }
                              >
                                Previous
                              </Button>
                              <Button
                                size="small"
                                disabled={identityPage >= identityPageCount - 1}
                                onClick={() =>
                                  setIdentityPage((current) => current + 1)
                                }
                              >
                                Next
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </>
              )}
              {issueSummary.map(
                ({ code, severity, count, action, rows: issueRows }) => {
                  const isExpanded = expandedCodes.has(code);
                  return (
                    <>
                      <TableRow
                        key={code}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => toggleExpand(code)}
                      >
                        <TableCell padding="checkbox">
                          <IconButton size="small" tabIndex={-1}>
                            {isExpanded ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {ISSUE_LABELS[code] || code}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {code}
                          </Typography>
                          {ISSUE_ACTIONS[code] && (
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{ mt: 0.5 }}
                            >
                              {ISSUE_ACTIONS[code]}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={700}>
                            {count.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={severity}
                            size="small"
                            sx={CHIP_SX[severity] || CHIP_SX.MEDIUM}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={action}
                            size="small"
                            variant="outlined"
                            color={action === "Excluded" ? "error" : "warning"}
                          />
                        </TableCell>
                      </TableRow>

                      {/* Expandable detail rows — spans all columns */}
                      <TableRow key={`${code}-detail`} sx={{ p: 0 }}>
                        <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                          <Collapse in={isExpanded} unmountOnExit>
                            <IssueDetailRows
                              issueCode={code}
                              rows={issueRows}
                            />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </>
                  );
                },
              )}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default CsvValidationPanel;
